import { Alert, Button, Descriptions, Input, Tag } from 'antd'
import dayjs from 'dayjs'
import {
  needsJustification,
  type OverrideJustification,
  type OverrideValue,
} from 'features/libra/overrides/overrideJustification'
import { useGeckoPermissions } from 'features/permissions'
import { Pencil } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import styled from 'styled-components'

import { JustificationDialog } from './JustificationDialog'

export interface OverridableFieldProps<T extends OverrideValue> {
  /** Names the value in the dialog title, e.g. "Total surface area". */
  label: string
  calculatedValue: T | undefined
  overrideValue: T | undefined
  justification: OverrideJustification | undefined
  formatValue: (value: T) => string
  /** Whether an edited value equals the calculated one; defaults to `===`. */
  isSameValue?: (a: T, b: T) => boolean
  renderEditor: (
    value: T | undefined,
    onChange: (value: T | undefined) => void
  ) => ReactNode
  /** `undefined` clears the override; an override carries its justification. */
  onChange: (
    value: T | undefined,
    justification?: OverrideJustification
  ) => void
  readonly?: boolean
}

/**
 * A calculated value an SME may override, only with a justification: the
 * field opens a dialog to override or reset it. Until a value is calculated
 * there is nothing to override, so it is a plain input.
 */
export function OverridableField<T extends OverrideValue>({
  label,
  calculatedValue,
  overrideValue,
  justification,
  formatValue,
  isSameValue = (a, b) => a === b,
  renderEditor,
  onChange,
  readonly,
}: OverridableFieldProps<T>) {
  const { user } = useGeckoPermissions()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [draft, setDraft] = useState<T>()
  const [draftText, setDraftText] = useState('')

  if (calculatedValue === undefined && !readonly) {
    return (
      <EditorSlot>
        {renderEditor(overrideValue, (value) => onChange(value))}
      </EditorSlot>
    )
  }

  const isOverridden =
    overrideValue !== undefined && calculatedValue !== undefined
  const effectiveValue = overrideValue ?? calculatedValue
  const calculatedText =
    calculatedValue === undefined ? undefined : formatValue(calculatedValue)
  const isMissingJustification = needsJustification(
    overrideValue,
    calculatedValue,
    justification
  )
  const justificationNote =
    !isMissingJustification && justification
      ? ` · “${justification.text}” — ${justification.justifiedBy}, ${dayjs(justification.justifiedAt).format('MMM D, YYYY h:mm A')}`
      : ''

  const openDialog = () => {
    setDraft(effectiveValue)
    setDraftText('')
    setDialogOpen(true)
  }

  const submit = (text: string) => {
    if (draft === undefined) return
    onChange(draft, {
      text,
      justifiedBy: user?.name || user?.email || '',
      justifiedAt: new Date().toISOString(),
      overrideValue: draft,
    })
  }

  const reset = () => {
    onChange(undefined)
    setDialogOpen(false)
  }

  return (
    <Container>
      <ValueField
        readOnly
        disabled={readonly}
        value={effectiveValue === undefined ? '' : formatValue(effectiveValue)}
        onClick={readonly ? undefined : openDialog}
        suffix={
          <>
            {isOverridden && <Tag color='orange'>Overridden</Tag>}
            {!readonly && (
              <Button
                type='text'
                size='small'
                aria-label={`Override ${label}`}
                icon={<Pencil size={14} />}
                onClick={openDialog}
              />
            )}
          </>
        }
      />

      {isOverridden && (
        <Hint>
          Calculated: {calculatedText}
          {justificationNote}
        </Hint>
      )}
      {isMissingJustification && (
        <Alert
          type='warning'
          showIcon
          message='This override needs a justification.'
        />
      )}

      <JustificationDialog
        open={dialogOpen}
        title={`${label} override`}
        inputLabel='Justification'
        placeholder='Why should this value replace the calculated one?'
        minLength={0}
        maxLength={2000}
        submitLabel='Save'
        text={draftText}
        onTextChange={setDraftText}
        submitDisabled={
          draft === undefined ||
          (calculatedValue !== undefined && isSameValue(draft, calculatedValue))
        }
        onSubmit={submit}
        onClose={() => setDialogOpen(false)}
        footerStart={
          isOverridden && <Button onClick={reset}>Reset to calculated</Button>
        }>
        <ValuesTable
          bordered
          column={1}
          size='small'
          items={[
            {
              key: 'calculated',
              label: 'Calculated',
              children: calculatedText,
            },
            {
              key: 'override',
              label: 'Override to',
              children: (
                <EditorSlot>{renderEditor(draft, setDraft)}</EditorSlot>
              ),
            },
          ]}
        />
      </JustificationDialog>
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const ValueField = styled(Input)`
  input:read-only:not(:disabled) {
    cursor: pointer;
  }

  .ant-tag {
    margin-inline-end: 0.25rem;
  }
`

const Hint = styled.span`
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.forecast.secondaryText};
`

const ValuesTable = styled(Descriptions)`
  margin-bottom: 1.5rem;
`

const EditorSlot = styled.div`
  > * {
    width: 100%;
  }
`
